import { useState } from "react"
import { Head, useForm } from "@inertiajs/react"
import { Label } from "@/components/ui/label"
import { Card, CardContent } from "@/components/ui/card"
import { Header } from "@/components/header"
import { UploadDropzone } from "@/components/upload-dropzone"
import { FileUploadCard } from "@/components/file-upload-card"
import { SampleFormFields } from "@/components/sample-form"
import type { UploadedFile } from "@/services"
import type { TagOption } from "@/types"

type Props = {
  available_keys: string[]
  popular_tags: TagOption[]
}

export default function New({ available_keys, popular_tags }: Props) {
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null)
  const [customTags, setCustomTags] = useState<string[]>([])

  const { data, setData, post, processing, errors, transform } = useForm({
    title: "",
    description: "",
    file_key: "",
    file_name: "",
    file_size: 0,
    content_type: "",
    bpm: "",
    key: "",
    tag_ids: [] as number[],
    new_tags: [] as string[],
  })

  const handleUploadComplete = (file: UploadedFile) => {
    setUploadedFile(file)
    setData({
      ...data,
      file_key: file.key,
      file_name: file.fileName,
      file_size: file.fileSize,
      content_type: file.contentType,
      title: data.title || file.fileName.replace(/\.[^/.]+$/, ""),
    })
  }

  const resetUpload = () => {
    setUploadedFile(null)
    setData({
      ...data,
      file_key: "",
      file_name: "",
      file_size: 0,
      content_type: "",
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    transform((data) => ({ sample: data }))
    post("/samples")
  }

  return (
    <div className="min-h-screen">
      <Head title="Upload Sample" />
      <Header />

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <h1 className="text-2xl font-mono font-bold mb-6">UPLOAD SAMPLE</h1>

        <form onSubmit={handleSubmit}>
          <Card>
            <CardContent className="p-6 space-y-6">
              {!uploadedFile ? (
                <div>
                  <Label className="mb-2 block">Audio File</Label>
                  <UploadDropzone
                    onUploadComplete={handleUploadComplete}
                    onUploadError={(err) => console.error(err)}
                  />
                  {errors.file_key && (
                    <p className="text-sm text-destructive mt-1">{errors.file_key}</p>
                  )}
                </div>
              ) : (
                <>
                  <FileUploadCard file={uploadedFile} onRemove={resetUpload} />

                  <SampleFormFields
                    data={data}
                    errors={errors}
                    onChange={setData}
                    availableKeys={available_keys}
                    popularTags={popular_tags}
                    customTags={customTags}
                    onCustomTagsChange={(tags) => {
                      setCustomTags(tags)
                      setData("new_tags", tags)
                    }}
                    submitLabel={processing ? "Publishing..." : "Publish Sample"}
                    processing={processing}
                    onCancel={resetUpload}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </form>
      </main>
    </div>
  )
}
